import React from 'react';
import { ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

export default function CategoriesPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const cards = [
    {
      titleKey:     "categories.clothes.title",
      subtitleKey:  "categories.clothes.subtitle",
      backTitleKey: "categories.clothes.back_title",
      detailsKey:   "categories.clothes.details",
      tagsKey:      "categories.clothes.tags",
      image:        "clothes.png",
      link:         "/clothes",
      accent:       "#dfcc25",
      emoji:        "👗",
    },
    {
      titleKey:     "categories.kitchen.title",
      subtitleKey:  "categories.kitchen.subtitle",
      backTitleKey: "categories.kitchen.back_title",
      detailsKey:   "categories.kitchen.details",
      tagsKey:      "categories.kitchen.tags",
      image:        "kitchen.png",
      link:         "/kitchen",
      accent:       "#c8a98a",
      emoji:        "🍽️",
    },
    {
      titleKey:     "categories.makeup.title",
      subtitleKey:  "categories.makeup.subtitle",
      backTitleKey: "categories.makeup.back_title",
      detailsKey:   "categories.makeup.details",
      tagsKey:      "categories.makeup.tags",
      image:        "makeup.png",
      link:         "/makeup",
      accent:       "#e8a0b0",
      emoji:        "💄",
    },
    {
      titleKey:     "categories.jewellery.title",
      subtitleKey:  "categories.jewellery.subtitle",
      backTitleKey: "categories.jewellery.back_title",
      detailsKey:   "categories.jewellery.details",
      tagsKey:      "categories.jewellery.tags",
      image:        "jewellery.png",
      link:         "/jewellery",
      accent:       "#b8960c",
      emoji:        "💎",
    },
  ];

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Poiret+One&display=swap');

        .flip-card {
          perspective: 1200px;
          cursor: pointer;
        }
        .flip-card-inner {
          position: relative;
          width: 100%;
          height: 100%;
          transform-style: preserve-3d;
          transition: transform 0.75s cubic-bezier(0.4, 0, 0.2, 1);
        }
        .flip-card:hover .flip-card-inner {
          transform: rotateY(180deg);
        }
        .flip-card-front,
        .flip-card-back {
          position: absolute;
          inset: 0;
          backface-visibility: hidden;
          -webkit-backface-visibility: hidden;
          border-radius: 30px;
          overflow: hidden;
        }
        .flip-card-back {
          transform: rotateY(180deg);
        }
        .tag-pill {
          display: inline-block;
          padding: 3px 11px;
          border-radius: 999px;
          font-size: 10px;
          font-family: 'Poiret One', cursive;
          letter-spacing: 0.08em;
          color: rgba(255,255,255,0.8);
          background: rgba(255,255,255,0.08);
          border: 1px solid rgba(255,255,255,0.18);
          backdrop-filter: blur(4px);
        }
        .cat-go-btn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          width: 100%;
          padding: 11px 0;
          border-radius: 999px;
          background: rgba(255,255,255,0.1);
          border: 1.5px solid rgba(255,255,255,0.25);
          color: #fff;
          font-family: 'Poiret One', cursive;
          font-size: 13px;
          letter-spacing: 0.14em;
          text-transform: uppercase;
          cursor: pointer;
          transition: all 0.3s ease;
          backdrop-filter: blur(8px);
        }
        .cat-go-btn:hover {
          background: rgba(255,255,255,0.22);
          border-color: rgba(255,255,255,0.6);
          transform: translateY(-2px);
          box-shadow: 0 8px 24px rgba(0,0,0,0.25);
        }
        .cat-go-btn svg {
          transition: transform 0.3s ease;
        }
        .cat-go-btn:hover svg {
          transform: translateX(4px);
        }
      `}</style>

      <div className="min-h-screen flex items-center justify-center p-6 md:p-10">
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-8 max-w-7xl w-full">
          {cards.map((card, index) => (
            <FlipCard key={index} card={card} t={t} navigate={navigate} />
          ))}
        </div>
      </div>
    </>
  );
}

function FlipCard({ card, t, navigate }) {
  const tags = t(card.tagsKey, { returnObjects: true });
  const tagsArray = Array.isArray(tags) ? tags : [];

  return (
    <div className="flip-card" style={{ height: "520px" }}>
      <div className="flip-card-inner">

        {/* ── FRONT ── */}
        <div className="flip-card-front shadow-xl">
          {/* Background image */}
          <img
            src={card.image}
            alt={t(card.titleKey)}
            style={{
              position: "absolute",
              inset: 0,
              width: "100%",
              height: "100%",
              objectFit: "cover",
            }}
          />

          {/* Dark gradient from bottom */}
          <div style={{
            position: "absolute",
            inset: 0,
            background: "linear-gradient(to top, rgba(8,16,14,0.90) 0%, rgba(8,16,14,0.25) 55%, transparent 100%)",
          }} />

          {/* Top-right gold ribbon */}
          <img
            src="/ChatGPT_Image_22_avr._2026__11_22_16-removebg-preview.png"
            alt=""
            aria-hidden="true"
            style={{
              position: "absolute",
              top: 0,
              right: 0,
              width: 120,
              height: 110,
              objectFit: "contain",
              objectPosition: "top right",
              pointerEvents: "none",
              zIndex: 10,
            }}
          />

          {/* Top-right emoji badge */}
          <div style={{
            position: "absolute", top: 18, right: 18,
            width: 42, height: 42, borderRadius: "50%",
            background: "rgba(255,255,255,0.13)",
            backdropFilter: "blur(8px)",
            border: "1px solid rgba(255,255,255,0.22)",
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 20,
            zIndex: 11,
          }}>
            {card.emoji}
          </div>

          {/* Hover hint */}
          <p style={{
            position: "absolute", top: 20, left: 20,
            fontSize: 9, fontFamily: "'Poiret One', cursive",
            letterSpacing: "0.22em", textTransform: "uppercase",
            color: "rgba(255,255,255,0.38)",
          }}>
            {t("categories.hover_hint")}
          </p>

          {/* Bottom text block */}
          <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, padding: "28px 26px" }}>
            {/* Accent bar */}
            <div style={{
              width: 34, height: 2.5, borderRadius: 2,
              background: card.accent, marginBottom: 12,
            }} />

            <h2 style={{
              fontFamily: "'Poiret One', cursive",
              fontSize: 25, fontWeight: 700,
              letterSpacing: "0.28em", textTransform: "uppercase",
              color: "#fff", marginBottom: 8,
            }}>
              {t(card.titleKey)}
            </h2>

            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <div style={{ width: 20, height: 1, background: card.accent, opacity: 0.7 }} />
              <p style={{
                fontSize: 10, fontFamily: "'Poiret One', cursive",
                letterSpacing: "0.28em", textTransform: "uppercase",
                color: "rgba(255,255,255,0.55)",
              }}>
                {t(card.subtitleKey)}
              </p>
            </div>
          </div>
        </div>

        {/* ── BACK ── */}
        <div
          className="flip-card-back"
          style={{
            background: "linear-gradient(150deg, #0c1e1b 0%, #1a3330 55%, #0c1e1b 100%)",
            display: "flex",
            flexDirection: "column",
            padding: "30px 26px",
            justifyContent: "space-between",
          }}
        >
          {/* Top accent line */}
          <div style={{
            position: "absolute", top: 0, left: 0, right: 0, height: 3,
            background: `linear-gradient(90deg, transparent, ${card.accent}, transparent)`,
          }} />

          {/* Corner brackets */}
          {[
            { top:14, left:14,  borderTop:`1.5px solid ${card.accent}`, borderLeft:`1.5px solid ${card.accent}` },
            { top:14, right:14, borderTop:`1.5px solid ${card.accent}`, borderRight:`1.5px solid ${card.accent}` },
            { bottom:14, left:14,  borderBottom:`1.5px solid ${card.accent}`, borderLeft:`1.5px solid ${card.accent}` },
            { bottom:14, right:14, borderBottom:`1.5px solid ${card.accent}`, borderRight:`1.5px solid ${card.accent}` },
          ].map((s, i) => (
            <div key={i} style={{ position:"absolute", width:16, height:16, opacity:0.6, ...s }} />
          ))}

          {/* Upper content */}
          <div>
            {/* Icon + heading row */}
            <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:18 }}>
              <div style={{
                width:46, height:46, borderRadius:"50%", flexShrink:0,
                background:"rgba(255,255,255,0.06)",
                border:`1.5px solid ${card.accent}55`,
                display:"flex", alignItems:"center", justifyContent:"center",
                fontSize:22,
              }}>
                {card.emoji}
              </div>
              <div>
                <p style={{
                  fontSize: 9, fontFamily:"'Poiret One', cursive",
                  letterSpacing:"0.3em", textTransform:"uppercase",
                  color: card.accent, marginBottom:3,
                }}>
                  {t(card.subtitleKey)}
                </p>
                <h3 style={{
                  fontFamily:"'Poiret One', cursive",
                  fontSize:20, fontWeight:700,
                  letterSpacing:"0.18em", textTransform:"uppercase",
                  color:"#fff",
                }}>
                  {t(card.backTitleKey)}
                </h3>
              </div>
            </div>

            {/* Divider */}
            <div style={{
              height:1, marginBottom:16,
              background:`linear-gradient(90deg, ${card.accent}60, transparent)`,
            }} />

            {/* Description */}
            <p style={{
              fontSize:13, fontFamily:"'Poiret One', cursive",
              color:"rgba(255,255,255,0.6)", lineHeight:1.85,
              letterSpacing:"0.03em", marginBottom:18,
            }}>
              {t(card.detailsKey)}
            </p>

            {/* Tag pills */}
            {tagsArray.length > 0 && (
              <div style={{ display:"flex", flexWrap:"wrap", gap:6 }}>
                {tagsArray.map((tag, i) => (
                  <span
                    key={i}
                    className="tag-pill"
                    style={{ borderColor:`${card.accent}44` }}
                  >
                    {tag}
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* CTA */}
          <div style={{ marginTop:20 }}>
            {/* Divider above button */}
            <div style={{
              height:1, marginBottom:16,
              background:`linear-gradient(90deg, transparent, ${card.accent}40, transparent)`,
            }} />
            <button
              className="cat-go-btn"
              style={{ borderColor:`${card.accent}70` }}
              onClick={(e) => { e.stopPropagation(); navigate(card.link); }}
            >
              {t("categories.explore_btn")}
              <ArrowRight size={15} />
            </button>
          </div>
        </div>

      </div>
    </div>
  );
}